import type {
  BulkApproveFailureReason,
  OutstandingApprovalItem,
  OutstandingApprovalsFilter,
} from '@time-tracking/shared';
import { formatSubmissionDeadline } from '@time-tracking/shared';
import { useEffect, useRef, useState } from 'react';

import { useOutstandingApprovals } from '../../api/approvals.js';
import { CheckIcon } from '../icons/CheckIcon.js';
import { InfoIcon } from '../icons/InfoIcon.js';
import { SearchIcon } from '../icons/SearchIcon.js';
import { ActionTable } from './ActionTable.js';
import { nextFocusTarget, reconcileSelection } from './bulkApprove.js';
import {
  bulkTargets,
  isBulkApprovable,
  type ActionGroup,
  type GroupBy,
} from './needsActionGrouping.js';
import './needs-action.css';

const FILTERS: readonly {
  id: OutstandingApprovalsFilter;
  label: string;
  /** Badge tone. The prototype tones three of the five; see needs-action.css. */
  tone?: 'accent' | 'warn' | 'alert';
}[] = [
  { id: 'everything', label: 'Everything' },
  { id: 'needs_approval', label: 'Needs approval', tone: 'accent' },
  { id: 'not_submitted', label: 'Not submitted', tone: 'warn' },
  { id: 'waiting_on_fix', label: 'Waiting on fix' },
  { id: 'past_due', label: 'Past due', tone: 'alert' },
];

/** Long enough that typing a name is one request, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 250;

interface NeedsActionViewProps {
  scope: 'my_teams' | 'all_teams';
  isAdmin: boolean;
  currentUserId?: string | null;
  onOpenItem: (item: OutstandingApprovalItem) => void;
  /**
   * Must return a promise when it dispatches. `dispatchAndClaim` reads that as the
   * signal that the rows really were acted on, and both the focus rescue and the
   * "this change was mine" bookkeeping hang off it.
   */
  onApprove: (item: OutstandingApprovalItem) => unknown;
  onReturn: (item: OutstandingApprovalItem) => void;
  /** Returns a promise when it dispatches, and void when it opens a confirmation. */
  onApproveMany: (items: OutstandingApprovalItem[]) => unknown;
  onAssignTeam: (item: OutstandingApprovalItem, anchorRect: DOMRect) => void;
  onOpenWeek: (weekStartDate: string) => void;
  /** Announced politely when the list changes under an existing selection. */
  onAnnounce?: (message: string) => void;
  /** Rows a partially-failed bulk approve left unapproved; they stay selected. */
  keepSelectedKeys?: readonly string[] | null;
  /** Row key → why the last bulk approve refused it, rendered against the row (R23). */
  failureReasons?: Record<string, BulkApproveFailureReason>;
  /**
   * True while ANY multi-request batch is in flight — a chunked bulk approve, or a team
   * assignment fanning out across several people.
   *
   * A mutation's own `isPending` follows its latest call, so for either batch it reports
   * false part-way through and everything gated on it re-enables. A second bulk approve
   * started in that gap goes out under a fresh idempotency key, and the server, seeing
   * sheets the first run already approved, reports them skipped — so the two runs
   * produce a success toast and an "already handled by someone else" toast about the
   * same sheets, blaming a colleague for the viewer's own click. A second assignment
   * batch races the first's team writes.
   */
  isBatchPending?: boolean;
}

export function NeedsActionView({
  scope,
  isAdmin,
  currentUserId = null,
  onOpenItem,
  onApprove,
  onReturn,
  onApproveMany,
  onAssignTeam,
  onOpenWeek,
  onAnnounce,
  keepSelectedKeys,
  failureReasons = {},
  isBatchPending = false,
}: NeedsActionViewProps) {
  const [filter, setFilter] = useState<OutstandingApprovalsFilter>('everything');
  const [groupBy, setGroupBy] = useState<GroupBy>('person');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Debounced so a typed name is one request rather than one per keystroke.
  // Cleared on unmount: react-hooks/exhaustive-deps does not catch a leaked
  // timer, per docs/solutions/ui-bugs/toast-timeout-not-cleared-on-unmount-state-update-warning-2026-05-10.md.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Changing what is on screen clears the selection: a checkbox the viewer can no
  // longer see must not stay in a bulk action's target set.
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [filter, search, groupBy, scope]);

  // Grouping keys change meaning between modes (user id, Monday, team id), so a
  // carried-over collapsed set would collapse unrelated groups.
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupBy]);

  const query = useOutstandingApprovals(scope, filter, search);
  const data = query.data;
  const items = data?.items ?? [];
  // True while the rows on screen belong to the PREVIOUS filter, scope or search.
  //
  // `placeholderData: keepPreviousData` exists so the search box stays mounted while the
  // next page loads — unmounting it loses focus mid-word. The cost is a window in which
  // the chips and the scope toggle already show the new choice while the table still
  // holds the old rows, and every row action stays live: pressing "Not submitted" and
  // immediately approving a row approves a sheet the filter on screen claims to exclude.
  // (The bulk paths are separately safe — selection is cleared on every one of these
  // changes — but the per-row and per-group buttons are not.)
  //
  // So the table is inert and dimmed until the current key resolves, rather than the
  // search box being sacrificed for it. Both promises are then kept: the caret stays put
  // and nothing is actionable that the controls no longer describe.
  const stale = query.isPlaceholderData;
  // The typed query has not reached the results yet. `stale` cannot see this: the
  // debounce holds `search` at its old value, so the query key has not changed and
  // TanStack has nothing in flight to report — for those 250ms the search box shows one
  // thing and the table means another, with every row action live. It is the same defect
  // `stale` was added for, in the window immediately before `stale` can engage, so
  // covering only one of them left the seam between them open.
  const searchPending = searchInput !== search;
  // Nothing may be dispatched while the rows do not match the controls that describe
  // them, or while a bulk run is still going. Three reasons, one gate — every approve
  // path funnels through `dispatchAndClaim`, and the affordances below read the same
  // flag, so the visible state and the enforced state cannot disagree.
  const busy = stale || searchPending || isBatchPending;
  const rootRef = useRef<HTMLDivElement>(null);

  // Rows change underneath a selection while a manager works — focus-refetch is
  // deliberately left on so the list keeps telling the truth. A key that is no
  // longer approvable would inflate the bulk button and then fail on submit.
  // Rows the viewer themselves just acted on. Without this, a clean bulk approve fires
  // the success toast and then, because the approved rows have left data.items, the
  // reconcile announcement too — "3 selected sheets changed and were deselected" read
  // straight after "Approved 3 sheets" says someone else got there first, about sheets
  // the viewer approved a moment ago. The announcement is for genuine surprises.
  const ownActionKeys = useRef<Set<string>>(new Set());
  /**
   * Rows with a request in flight right now.
   *
   * Distinct from `ownActionKeys`, which records "this change was mine" for the
   * announcement and outlives the request. This is purely an in-flight guard: the ref is
   * the synchronous half that a double-click cannot beat, the state is what disables the
   * row's own buttons.
   */
  const inFlightKeys = useRef<Set<string>>(new Set());
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!data) return;

    // Release BEFORE reconciling, and outside the updater. `dispatchAndClaim` now stamps
    // only what was actually dispatched and releases on rejection, so the two cases this
    // used to carry alone — a cancelled confirmation and a failed request — no longer
    // reach here armed. It stays as the backstop for the keys nothing else releases: a
    // row approved by its own button while unselected is never in `droppedKeys` below,
    // so without this its key would sit in the set until unmount. A key still present
    // and still actionable in a fresh payload plainly wasn't approved.
    //
    // This sat inside setSelectedKeys behind `prev.size === 0` and `dropped === 0`,
    // which are exactly the states a solo approve or a cancelled bulk leaves behind —
    // so the release never ran in the two cases it exists for.
    for (const item of data.items) {
      if (item.selectable) ownActionKeys.current.delete(item.key);
    }

    setSelectedKeys((prev) => {
      if (prev.size === 0) return prev;
      const { next, dropped } = reconcileSelection(prev, data.items);
      if (dropped === 0) return prev;
      const droppedKeys = [...prev].filter((k) => !next.has(k));
      const surprises = droppedKeys.filter((k) => !ownActionKeys.current.has(k));
      for (const k of droppedKeys) ownActionKeys.current.delete(k);
      if (surprises.length > 0) {
        onAnnounce?.(
          `${surprises.length} selected ${surprises.length === 1 ? 'sheet' : 'sheets'} changed and ${
            surprises.length === 1 ? 'was' : 'were'
          } deselected.`,
        );
      }
      return next;
    });
  }, [data, onAnnounce]);

  // A partially-failed bulk approve leaves its failed rows selected, so the
  // manager can see which ones still need them.
  useEffect(() => {
    if (keepSelectedKeys) setSelectedKeys(new Set(keepSelectedKeys));
  }, [keepSelectedKeys]);

  /**
   * Approve one row and move focus somewhere sensible, since the row is about to
   * disappear. Scoped to the row that actually holds focus: a background refetch
   * removing an unrelated row must never move it.
   */
  /**
   * Bulk and group approves all destroy the control that triggered them: the hero
   * button re-renders `disabled` (which blurs a focused button to <body> in every
   * browser), the selection bar unmounts once the selection empties, and a group's
   * <tbody> pair disappears when its last item is approved. None of them can hand
   * focus to a "next row" the way a single-row approve can, because there is no
   * single row involved.
   *
   * So this rescues focus rather than steering it: it only acts once focus has
   * actually been lost to <body>, which means a background refetch that removes some
   * unrelated row never moves the caret out from under someone mid-task. The search
   * box is the target because it is the one control guaranteed to still be mounted —
   * it survives every zero-row state now that the no-match card renders inline.
   */
  /** When an action settled, and which rows it acted on — see the rescue effect. */
  const rescueArmed = useRef<{ at: number; keys: readonly string[] } | null>(null);
  const armRescue = (keys: readonly string[]): void => {
    rescueArmed.current = { at: Date.now(), keys };
  };

  /**
   * Rescue focus after an action that destroys the control which triggered it.
   *
   * Two earlier attempts got the timing wrong in opposite directions. Scheduling a
   * single `requestAnimationFrame` after dispatch fired long before the async request
   * returned, so it always concluded focus was fine. Arming at dispatch and waiting for
   * any `data` change then armed on actions that never dispatched (a cancelled confirm)
   * and could be consumed by an unrelated refetch.
   *
   * So: arm only once the action has SETTLED — which is also when the refetch that
   * removes the control is imminent — and let the arm expire, so a settled action that
   * changes nothing can never strand a flag that steals focus later.
   */
  /**
   * Run an action, and only then claim the rows it touched as ours.
   *
   * "Did this return a thenable?" is the one honest signal that something was actually
   * dispatched: the over-threshold bulk path returns void because it opens a
   * confirmation instead. That signal already gated the focus rescue, but the key
   * stamping ran unconditionally before the call — so cancelling the confirmation left
   * the keys armed, and the next genuine change to those rows was swallowed as ours,
   * silently costing a screen-reader user the "changed and was deselected" notice they
   * are owed. A rejection releases them again, for the same reason: a request that
   * failed did not move those rows.
   *
   * One signal, both consequences, rather than a second weaker heuristic per concern.
   */
  const dispatchAndClaim = (keys: readonly string[], act: () => unknown): void => {
    // Belt and braces behind the inert table and the disabled buttons: every approve
    // path in this view funnels through here, so one guard covers the hero, the
    // selection bar, the group headers and the rows.
    if (busy) return;
    // And a per-row guard, because `busy` does not cover a single row's own request.
    // The row's Approve button stayed live while its POST was in flight, so an ordinary
    // double-click sent two with different idempotency keys: the first approved the
    // sheet, the conditional update refused the second, and the viewer got "Approved"
    // and "Failed to approve" for one action. The ref is checked and written
    // synchronously — two clicks land in the same tick, before React can re-render the
    // button as disabled, so `disabled` alone cannot close this.
    if (keys.some((k) => inFlightKeys.current.has(k))) return;
    for (const k of keys) inFlightKeys.current.add(k);
    setInFlight(new Set(inFlightKeys.current));
    const release = (): void => {
      for (const k of keys) inFlightKeys.current.delete(k);
      setInFlight(new Set(inFlightKeys.current));
    };
    const hadFocusInside =
      document.activeElement instanceof HTMLElement &&
      rootRef.current?.contains(document.activeElement) === true;
    const result = act();
    if (typeof (result as { then?: unknown } | undefined)?.then !== 'function') {
      // Nothing was dispatched — an over-threshold bulk opened a confirmation instead.
      // Hold nothing: the rows are not in flight.
      release();
      return;
    }

    for (const k of keys) ownActionKeys.current.add(k);

    // Move focus out BEFORE the busy state lands, not after the fact.
    //
    // The group header's Approve button lives inside `.act-results` — unlike the hero
    // and selection-bar buttons, which sit outside it — so acting on a group makes the
    // button's own ancestor `inert`, and the browser answers that by blurring to
    // <body> for the whole run. Rescuing afterwards is too late and too conditional:
    // it waits for a data change that a wholly-failed run never produces. Stepping the
    // caret to the search box first means there is nothing to rescue.
    const active = document.activeElement;
    if (
      hadFocusInside &&
      active instanceof HTMLElement &&
      active.closest('.act-results') !== null
    ) {
      rootRef.current?.querySelector<HTMLInputElement>('.act-search input')?.focus();
    }

    void Promise.resolve(result).then(
      () => {
        release();
        if (hadFocusInside) armRescue(keys);
      },
      () => {
        release();
        for (const k of keys) ownActionKeys.current.delete(k);
        if (hadFocusInside) armRescue(keys);
      },
    );
  };

  // Fires on the first data change after the action settled. Rescues only when focus has
  // genuinely been lost, so a refetch that removes some unrelated row never moves the
  // caret. The arm expires: a settled action whose refetch changes nothing must not leave
  // a flag that hijacks focus minutes later.
  const RESCUE_WINDOW_MS = 5_000;
  useEffect(() => {
    const armed = rescueArmed.current;
    if (armed === null) return;
    // Wait for THIS action's rows to actually leave, not merely for the next payload of
    // any origin. A concurrent row action, a window-refocus refetch or the deadline
    // timer all produce a `data` change that would otherwise burn the arm early and
    // leave the real focus loss unrescued. Rows that are still listed mean the refetch
    // reflecting this action has not landed yet.
    const stillListed = (data?.items ?? []).some((i) => armed.keys.includes(i.key));
    if (stillListed && Date.now() - armed.at <= RESCUE_WINDOW_MS) return;
    rescueArmed.current = null;
    if (Date.now() - armed.at > RESCUE_WINDOW_MS) return;
    const active = document.activeElement;
    const lost =
      active === null ||
      active === document.body ||
      (active instanceof HTMLButtonElement && active.disabled);
    if (!lost) return;
    rootRef.current?.querySelector<HTMLInputElement>('.act-search input')?.focus();
  }, [data]);

  const approveManyAndRescue = (targets: OutstandingApprovalItem[]): void => {
    dispatchAndClaim(
      targets.map((t) => t.key),
      () => onApproveMany(targets),
    );
  };

  const approveAndRefocus = (item: OutstandingApprovalItem): void => {
    // Same reason as approveManyAndRescue: a row can be both checkbox-selected and
    // approved by its own button, and without the claim its disappearance is announced
    // back to the viewer as somebody else's change. Focus is moved here rather than by
    // the rescue, because this path knows exactly which row is going away.
    const root = rootRef.current;
    const active = document.activeElement;
    const holdsFocus =
      root !== null &&
      active instanceof HTMLElement &&
      active.closest(`[data-row-key="${CSS.escape(item.key)}"]`) !== null;
    const target = holdsFocus && root ? nextFocusTarget(root, item.key) : null;
    dispatchAndClaim([item.key], () => onApprove(item));
    if (target) target.focus();
  };

  // Built here rather than inline so the dependency on every settled input is explicit.
  const announcement = (() => {
    const count = `${String(items.length)} ${items.length === 1 ? 'result' : 'results'}`;
    const where = scope === 'all_teams' ? 'all teams' : 'your teams';
    // Reuses the chip table, so the announcement always names the chip the user
    // pressed rather than a second copy of the label that could drift from it.
    const filterLabel = FILTERS.find((f) => f.id === filter)?.label ?? 'Everything';
    return search
      ? `${count} for “${search}”, ${filterLabel}, ${where}.`
      : `${count}, ${filterLabel}, ${where}.`;
  })();

  if (query.isError) {
    // Never an empty state on failure: "Nothing needs your action" on a 500 tells
    // a manager their backlog is clear when the server is simply down.
    return (
      <div className="error-banner" role="alert">
        <p>Couldn&rsquo;t load what needs action.</p>
        <button
          type="button"
          className="btn btn--sm btn--light"
          onClick={() => void query.refetch()}
        >
          Try again
        </button>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="act-skeleton" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading what needs action…</span>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="act-skeleton__row" />
        ))}
      </div>
    );
  }

  const counts = data?.counts ?? { all: 0, approve: 0, nudge: 0, waiting: 0, overdue: 0 };
  const filtering = filter !== 'everything' || search.trim().length > 0;
  // From the server, over the WHOLE backlog — not derived from `items`, which is the
  // filtered and capped slice currently on screen.
  const weeksRepresented = data?.weeksRepresented ?? 0;
  const worstDaysPastDue = data?.oldestDaysPastDue ?? 0;
  // Approvable somewhere other than this filtered view: the pill count minus the rows
  // that are visible here but can never be bulk-approved.
  const elsewhereApprovable = Math.max(
    0,
    counts.approve - items.filter((i) => i.kind === 'approve' && (i.aobo || i.isSelf)).length,
  );
  const cleanTargets = bulkTargets(items, { cleanOnly: true });
  const anyApprovable = bulkTargets(items, { cleanOnly: false }).length > 0;
  // Rows the bulk action skips but whose own row buttons are live: an approver's own
  // sheet keeps its Approve (R22 bars it from bulk, not from being approved at all) and
  // an AOBO row keeps its Review. Counted separately because the hero note below used to
  // say "No approvable sheets in this view" whenever the BULK target set was empty,
  // while those very rows sat underneath with working buttons.
  const ownRowActionable = items.filter(
    (i) => i.kind === 'approve' && !i.aobo && !isBulkApprovable(i),
  ).length;
  const aoboRowActionable = items.filter((i) => i.kind === 'approve' && i.aobo).length;

  // Only states that make the CHROME pointless may replace the whole view: nobody in
  // scope, or a genuinely clear backlog. A no-match is NOT one of them — returning
  // early for it unmounts the hero, the filter pills, the group-by toggle and the
  // search box itself, so the empty card's own advice ("Try 'Everything', or clear
  // the search") names two controls that are no longer on the page. That leaves no
  // focusable way back: a dead end for pointer and keyboard alike. The no-match card
  // renders in the table's place further down instead, which is also what the
  // prototype does (approver-action.jsx:373 — its empty branch sits inside the
  // fragment, after .action-hero and .filter-bar).
  const noOneInScope = data !== undefined && data.scopeUserCount === 0;
  if (items.length === 0 && (noOneInScope || !filtering)) {
    return (
      <EmptyState
        variant={
          noOneInScope
            ? isAdmin && scope === 'my_teams'
              ? 'admin-no-approver-teams'
              : 'no-teams'
            : 'all-clear'
        }
      />
    );
  }

  const selectedItems = items.filter((i) => selectedKeys.has(i.key));
  const selectedClean = selectedItems.filter((i) => i.anomalies.length === 0);

  return (
    <div ref={rootRef}>
      <section className="act-hero" aria-label="What needs action">
        <div className="act-hero__count">
          <span className="act-hero__n">{data?.awaitingActionCount ?? 0}</span>
          <div>
            <p className="act-hero__title">
              {(data?.awaitingActionCount ?? 0) === 1 ? 'item needs action' : 'items need action'}
            </p>
            <p className="act-hero__sub">
              across {weeksRepresented} {weeksRepresented === 1 ? 'week' : 'weeks'} with outstanding
              items
              {worstDaysPastDue > 0 && (
                <>
                  {' · oldest is '}
                  <b>
                    {worstDaysPastDue} {worstDaysPastDue === 1 ? 'day' : 'days'} past due
                  </b>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="act-hero__stats">
          <button
            type="button"
            className="act-stat"
            aria-label={`Filter to the ${counts.approve} sheets needing approval`}
            onClick={() => setFilter('needs_approval')}
          >
            <span className="act-stat__v">{counts.approve}</span>
            <span className="act-stat__k">to approve</span>
          </button>
          <button
            type="button"
            className="act-stat act-stat--warn"
            aria-label={`Filter to the ${counts.nudge} weeks not submitted`}
            onClick={() => setFilter('not_submitted')}
          >
            <span className="act-stat__v">{counts.nudge}</span>
            <span className="act-stat__k">not submitted</span>
          </button>
          <button
            type="button"
            className="act-stat act-stat--alert"
            aria-label={`Filter to the ${counts.overdue} past-due items`}
            onClick={() => setFilter('past_due')}
          >
            <span className="act-stat__v">{counts.overdue}</span>
            <span className="act-stat__k">past due</span>
          </button>
        </div>

        <div className="act-hero__actions">
          {anyApprovable ? (
            <button
              type="button"
              className="btn primary"
              disabled={cleanTargets.length === 0 || busy}
              onClick={() => approveManyAndRescue(cleanTargets)}
              title="Approves the sheets with no warnings that are currently listed"
            >
              <CheckIcon /> Approve {cleanTargets.length} clean
            </button>
          ) : (
            // Rows exist but none can be approved here. A disabled "Approve 0" button
            // reads as broken; say why instead. The categorical sentence is only true of
            // the WHOLE backlog, so it is reserved for the unfiltered, uncapped case —
            // otherwise it sat beside its own "N to approve" stat and contradicted it.
            <p className="act-hero__note">
              {ownRowActionable > 0 || aoboRowActionable > 0
                ? // Rows here ARE actionable, just not in one click, so say that rather
                  // than "no approvable sheets" — which the buttons below flatly
                  // contradict. Naming which kind tells the manager why.
                  `Nothing here can be approved in bulk. ${
                    ownRowActionable > 0 && aoboRowActionable > 0
                      ? `Your own ${ownRowActionable === 1 ? 'sheet needs' : 'sheets need'} approving on ${ownRowActionable === 1 ? 'its' : 'their'} own row, and ${String(aoboRowActionable)} ${aoboRowActionable === 1 ? 'is' : 'are'} for another lead to approve.`
                      : ownRowActionable > 0
                        ? `Your own ${ownRowActionable === 1 ? 'sheet is' : 'sheets are'} excluded from bulk actions — approve ${ownRowActionable === 1 ? 'it' : 'them'} from the row.`
                        : `${String(aoboRowActionable)} ${aoboRowActionable === 1 ? 'is' : 'are'} for another lead to approve — open ${aoboRowActionable === 1 ? 'it' : 'them'} to review.`
                  }`
                : filtering || data?.capped === true || counts.approve > 0
                  ? `No approvable sheets in this view${
                      // counts.approve includes AOBO and the viewer's own rows, which are
                      // on screen and not approvable anywhere — calling them "elsewhere"
                      // sent a manager looking for sheets that do not exist.
                      elsewhereApprovable > 0 ? ` — ${String(elsewhereApprovable)} elsewhere.` : '.'
                    }`
                  : 'Nothing to approve right now — every outstanding week is waiting on someone else to submit or resubmit.'}
            </p>
          )}
        </div>
      </section>

      <div className="act-filters">
        <div className="act-search">
          <SearchIcon />
          <input
            type="search"
            value={searchInput}
            placeholder="Search by name or EID…"
            aria-label="Search by name or EID"
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        {/* NOT role="tablist": that promises roving tabindex, arrow-key switching and an
            associated tabpanel, none of which exist here — AT would announce "tab, 1 of 5"
            and imply keys that do nothing. These are single-select toggles, which is
            exactly what aria-pressed describes. The prototype uses tablist; a11y wins. */}
        <div className="act-pills" role="group" aria-label="What needs action">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              className={`act-pill${filter === f.id ? ' act-pill--active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              <span
                className={`act-pill__badge${f.tone === undefined ? '' : ` act-pill__badge--${f.tone}`}`}
                aria-label={`${countFor(counts, f.id)} items`}
              >
                {countFor(counts, f.id)}
              </span>
            </button>
          ))}
        </div>

        <div className="act-groupby" role="group" aria-label="Group by">
          <span className="act-groupby__label">Group by</span>
          {(
            [
              'person',
              'week',
              ...(isAdmin && scope === 'all_teams' ? (['team'] as const) : []),
            ] as GroupBy[]
          ).map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={groupBy === g}
              className={`act-groupby__btn${groupBy === g ? ' act-groupby__btn--active' : ''}`}
              onClick={() => setGroupBy(g)}
            >
              {g === 'person' ? 'Person' : g === 'week' ? 'Week' : 'Team'}
            </button>
          ))}
        </div>
      </div>

      {data?.capped && (
        <p className="act-overflow" role="status">
          Showing the {items.length} most urgent of {countFor(counts, filter)} items. Narrow the
          filter or search to see the rest.
        </p>
      )}

      {/* Always mounted, and separate from the visible bar below.
          A live region that appears with its text already in it is unreliable — NVDA and
          VoiceOver announce CHANGES to a region that was already present, so mounting one
          pre-populated is frequently silent. The result announcement further down has
          been doing this correctly all along; the selection count was the copy that
          conditionally mounted, so the first sheet a keyboard user selected was the one
          change they were never told about. The visible toolbar stays conditional — it is
          the announcement that has to persist, not the chrome. */}
      <p className="sr-only" role="status" data-testid="selection-announcement">
        {selectedItems.length > 0
          ? `${String(selectedItems.length)} ${
              selectedItems.length === 1 ? 'sheet' : 'sheets'
            } selected, ${String(selectedClean.length)} clean, ${String(
              selectedItems.length - selectedClean.length,
            )} with warnings.`
          : ''}
      </p>

      {selectedItems.length > 0 && (
        <div className="act-selection">
          <span className="act-selection__count">{selectedItems.length}</span>
          <span className="act-selection__label">
            {selectedItems.length === 1 ? 'sheet' : 'sheets'} selected · {selectedClean.length}{' '}
            clean, {selectedItems.length - selectedClean.length} with warnings
          </span>
          <div className="act-selection__actions">
            <button
              type="button"
              className="btn btn--sm btn--light"
              onClick={() => setSelectedKeys(new Set())}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn--sm btn--light"
              disabled={selectedClean.length === 0 || busy}
              onClick={() => approveManyAndRescue(selectedClean)}
            >
              Approve clean only ({selectedClean.length})
            </button>
            <button
              type="button"
              className="btn btn--sm primary"
              disabled={busy}
              onClick={() => approveManyAndRescue(selectedItems)}
            >
              <CheckIcon /> Approve {selectedItems.length}
            </button>
          </div>
        </div>
      )}

      {/* 4.1.3: the debounced search and the filter chips both rewrite the table with no
          announcement, so a screen-reader user typing a name has no way to know the
          result set changed short of re-exploring it. Keyed on the settled values, not
          the keystroke, so it speaks once per result rather than once per letter.

          The message names what produced the count, not just the count. A live region
          re-announces on TEXT change, so two searches that each return three results
          left the string byte-identical and nothing was spoken at all — silence that
          reads as "your typing did nothing" rather than as "same number, different
          people". Naming the settled search, filter and scope makes every distinct
          result set a distinct string. */}
      <p className="sr-only" role="status" data-testid="result-announcement">
        {announcement}
      </p>

      {items.length === 0 ? (
        <EmptyState variant="no-match" />
      ) : (
        <div
          className={busy ? 'act-results act-results--busy' : 'act-results'}
          aria-busy={busy}
          inert={busy}
        >
          <ActionTable
            items={items}
            groupBy={groupBy}
            currentUserId={currentUserId}
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            collapsedGroups={collapsedGroups}
            onToggleGroup={(key) =>
              setCollapsedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              })
            }
            onOpenItem={onOpenItem}
            onApprove={approveAndRefocus}
            onReturn={onReturn}
            onApproveGroup={(group: ActionGroup) =>
              approveManyAndRescue(bulkTargets(group.items, { cleanOnly: false }))
            }
            onAssignTeam={onAssignTeam}
            onOpenWeek={onOpenWeek}
            failureReasons={failureReasons}
            inFlightKeys={inFlight}
          />
        </div>
      )}

      <p className="act-footnote">
        <InfoIcon />
        <span>
          Every week stays here until it is approved or its reporting period is closed.{' '}
          <b>Approving</b> publishes those hours to the cost-center export immediately. Hours are
          due {data ? formatSubmissionDeadline(data.deadline) : 'Monday 5:00 PM'}. Unsubmitted weeks
          are listed for visibility — the app doesn&rsquo;t send reminders.
        </span>
      </p>
    </div>
  );
}

function countFor(
  counts: { all: number; approve: number; nudge: number; waiting: number; overdue: number },
  filter: OutstandingApprovalsFilter,
): number {
  switch (filter) {
    case 'needs_approval':
      return counts.approve;
    case 'not_submitted':
      return counts.nudge;
    case 'waiting_on_fix':
      return counts.waiting;
    case 'past_due':
      return counts.overdue;
    default:
      return counts.all;
  }
}

const EMPTY_COPY = {
  'all-clear': {
    heading: 'Nothing needs your action',
    body: 'Every open week is approved or submitted. New sheets will land here as they arrive.',
  },
  'no-match': {
    heading: 'Nothing matches this filter',
    body: 'Try “Everything”, or clear the search.',
  },
  'no-teams': {
    heading: 'You don’t approve for any team yet',
    body: 'An administrator assigns approver access per team. Ask one to add you.',
  },
  'admin-no-approver-teams': {
    heading: 'You don’t approve for any team yet',
    body: 'Switch to All teams to see the whole department, or ask an administrator to add you as an approver.',
  },
} as const;

function EmptyState({ variant }: { variant: keyof typeof EMPTY_COPY }) {
  const { heading, body } = EMPTY_COPY[variant];
  // A clear backlog is the one genuinely good outcome in this view, so it gets the
  // affirmative mark; everything else is a "nothing here" and gets the search glyph.
  const cleared = variant === 'all-clear';
  return (
    <div className={`act-empty act-empty--${variant}`}>
      <span
        className={`act-empty__mark${cleared ? ' act-empty__mark--clear' : ''}`}
        aria-hidden="true"
      >
        {cleared ? <CheckIcon /> : <SearchIcon />}
      </span>
      <h3>{heading}</h3>
      <p>{body}</p>
    </div>
  );
}
